import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { createServiceConfig } from '../store/slices/serviceConfigSlice';
import { addNotification } from '../store/slices/uiSlice';
import { KNOWN_SERVICES, MODES, ConfigMode } from '../api/serviceConfigApi';
import { FiArrowLeft, FiSave, FiPlus, FiTrash2 } from 'react-icons/fi';

const EXTRA_CONFIG_PLACEHOLDER = `{
  "Monitoring": {
    "EnableHealthChecks": true,
    "Tracing": { "Enabled": true }
  }
}`;

const ServiceConfigCreatePage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const [serviceId, setServiceId] = useState('global');
    const [customService, setCustomService] = useState('');
    const [mode, setMode] = useState<ConfigMode>('default');
    const [topics, setTopics] = useState<string[]>([]);
    const [newTopic, setNewTopic] = useState('');
    const [extraJson, setExtraJson] = useState('');
    const [jsonError, setJsonError] = useState('');
    const [saving, setSaving] = useState(false);

    const resolvedService = serviceId === '__custom__' ? customService : serviceId;

    const validateJson = (v: string) => {
        if (!v.trim()) { setJsonError(''); return true; }
        try { JSON.parse(v); setJsonError(''); return true; }
        catch (e: any) { setJsonError(e.message); return false; }
    };

    const handleSubmit = async () => {
        if (!resolvedService) { return; }
        if (!validateJson(extraJson)) return;

        setSaving(true);
        try {
            const extra = extraJson.trim() ? JSON.parse(extraJson) : {};
            const payload = {
                serviceId: resolvedService,
                mode,
                ...(topics.length > 0 ? { subscribedTopics: topics } : {}),
                ...extra,
            };
            const result = await dispatch(createServiceConfig(payload)).unwrap();
            dispatch(addNotification({ type: 'success', message: `Config "${resolvedService}:${mode}" created.` }));
            navigate(`/service-configs/${result._id}`);
        } catch (e: any) {
            dispatch(addNotification({ type: 'error', message: e.message }));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <button onClick={() => navigate('/service-configs')}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">
                    <FiArrowLeft className="mr-2" /> Back
                </button>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">New Service Config</h1>
            </div>

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 space-y-6">
                {/* Identity */}
                <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Identity</h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                        <div className="sm:col-span-3">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Service</label>
                            <select value={serviceId} onChange={e => setServiceId(e.target.value)}
                                className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md">
                                {KNOWN_SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
                                <option value="__custom__">— custom —</option>
                            </select>
                        </div>
                        {serviceId === '__custom__' && (
                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Custom service ID</label>
                                <input type="text" value={customService} onChange={e => setCustomService(e.target.value)}
                                    placeholder="my-service"
                                    className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                            </div>
                        )}
                        <div className="sm:col-span-3">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Mode</label>
                            <select value={mode} onChange={e => setMode(e.target.value as ConfigMode)}
                                className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md">
                                {MODES.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                            <p className="mt-1 text-xs text-gray-400">
                                <strong>default</strong> applies across all modes. Specific modes override it.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Subscribed Topics */}
                <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Subscribed Topics</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Dapr pub/sub topics this service listens to.</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                        {topics.map((t, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs font-mono">
                                {t}
                                <button type="button" onClick={() => setTopics(topics.filter((_, j) => j !== i))}
                                    className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input type="text" value={newTopic} onChange={e => setNewTopic(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && newTopic.trim()) { setTopics([...topics, newTopic.trim()]); setNewTopic(''); } }}
                            placeholder="topic-name (press Enter to add)"
                            className="block flex-1 text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                        <button type="button" onClick={() => { if (newTopic.trim()) { setTopics([...topics, newTopic.trim()]); setNewTopic(''); } }}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">
                            <FiPlus className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Additional Config (JSON) */}
                <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Additional Configuration</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                        JSON object with any extra sections (Monitoring, Logging, SchedulerSettings, security, etc.).
                        Leave blank if not needed.
                    </p>
                    <textarea
                        value={extraJson}
                        onChange={e => { setExtraJson(e.target.value); validateJson(e.target.value); }}
                        rows={16}
                        placeholder={EXTRA_CONFIG_PLACEHOLDER}
                        spellCheck={false}
                        className={`w-full font-mono text-xs border rounded-md p-3 dark:bg-gray-900 dark:text-gray-100 ${jsonError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                    />
                    {jsonError && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{jsonError}</p>}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button onClick={() => navigate('/service-configs')}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">
                        Cancel
                    </button>
                    <button onClick={handleSubmit} disabled={saving || !!jsonError || !resolvedService}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50">
                        {saving ? 'Saving…' : <><FiSave className="mr-2" />Create</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ServiceConfigCreatePage;
